// „Vorgänge" — reine Vorgangs-Liste mit Pill-Cluster (Heute · Woche · Info ·
// Alle offen/erledigt) und zwei Segmented-Leisten im Kopf: neben der Überschrift
// die Art (Kontakt-Vorgänge/Persönliche), rechts der Bereich (Alle/Team/Eigene) —
// den gibt es nur bei Kontakt-Vorgängen; persönliche sind immer die eigenen.
// Jeder Vorgang lässt sich ausklappen, um SEINE offenen To-dos zu sehen
// (standalone-To-dos ohne Vorgang werden bewusst nicht gezeigt). Ein Vorgang
// landet in einem Cluster, wenn SEINE eigene Frist/TWV passt ODER eines seiner
// offenen To-dos passt (Vorgangs-TWV zuerst) — die Cluster-Prädikate stammen
// aus dem geteilten useTodoClusters-Hook (matchesCluster), damit Karte und
// Vorgänge-Seite dieselbe Logik nutzen. Über den Pills sucht ein Feld in
// Titel, Kunde und Vorgangsnummer (T-153, `vorgangSuche.ts`).
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
    Layer20Regular,
    Circle20Regular,
    Flag16Regular,
    Clock16Regular,
    ChevronDown20Regular,
    ChevronRight20Regular,
    Search16Regular,
} from "@fluentui/react-icons";
import { useResponsive } from "../../hooks/useResponsive";
import { type VorgangDto, type TodoDto } from "../../services/vorgangService";
import {
    dashboardVorgangSichtbar,
    bereichWaehlbar,
    kundeName as kundeNameOf,
    ART_OPTIONS,
    DEFAULT_ART,
    SCOPE_OPTIONS,
    type ScopeKey,
    type ScopeCtx,
    type VorgangArtKey,
} from "../../utils/vorgangScope";
import { REFINED } from "../risikoanalyseStep/tokens";
import {
    DashCard,
    Segmented,
    FilterPill,
    StatusChip,
    EmptyHint,
    MehrLadenButton,
    deDate,
    isOverdueFrist,
} from "./DashboardShared";
import {
    matchesCluster,
    TODO_CLUSTERS,
    DEFAULT_CLUSTER,
    type ClusterItem,
    type ClusterKey,
} from "./useTodoClusters";
import { deriveVorgangStatus as deriveVorgangStatusShared } from "../vorgang/shared/vorgangStatus";
import { passtZurVorgangSuche, vorgangSuchTokens } from "./vorgangSuche";

/** P2: Seitengröße fürs client-seitige Fenstern langer Listen. */
const PAGE_SIZE = 20;

/** Zweistufige Ableitung — SSOT ist `vorgang/shared/vorgangStatus.ts`; das frühere
 *  lokale Duplikat hier ignorierte `statusManuell` und wich bei To-do-losen
 *  Vorgängen von Liste und Detailseite ab. */
const deriveVorgangStatus = (v: VorgangDto): string =>
    deriveVorgangStatusShared({
        fortschrittErledigt: v.fortschrittErledigt,
        fortschrittGesamt: v.fortschrittGesamt,
        statusManuell: v.statusManuell,
        status: v.status,
    });

/** Ein Vorgang als Listen-Item inkl. seiner offenen (nicht erledigten) To-dos. */
interface VorgangItem {
    id: number;
    titel: string;
    kunde: string;
    vorgangNbr?: string | null;
    status: string;
    frist?: string | null;
    twv?: string | null;
    erledigt: number;
    gesamt: number;
    openTodos: TodoDto[];
}

/** ClusterItem-Sicht auf den Vorgang selbst (kein `todo` → nie im Info-Cluster). */
const vorgangSelf = (v: VorgangItem): ClusterItem => ({
    status: v.status,
    frist: v.frist,
    twv: v.twv,
});

/** ClusterItem-Sicht auf ein einzelnes To-do (trägt `todo` für die Info-Auswertung). */
const todoAsClusterItem = (t: TodoDto): ClusterItem => ({
    status: t.status,
    frist: t.frist,
    twv: t.twv,
    todo: t,
});

/** Sortierschlüssel „Vorgangs-TWV zuerst", dann Frist. */
const vorgangSortKey = (v: VorgangItem): number => {
    const d = v.twv || v.frist;
    return d ? new Date(d).getTime() : Number.MAX_SAFE_INTEGER;
};

/** Sortierschlüssel eines To-dos (TWV zuerst, dann Frist). */
const todoSortKey = (t: TodoDto): number => {
    const d = t.twv || t.frist;
    return d ? new Date(d).getTime() : Number.MAX_SAFE_INTEGER;
};

export const MeineArbeitCard: React.FC<{
    vorgaenge: VorgangDto[];
    todos: TodoDto[];
    scopeCtx: ScopeCtx;
    onChanged: () => void;
    /**
     * Vorgänge/To-dos sind noch unterwegs (T-153). Bis dahin sagt die leere
     * Liste „Lädt …“ — vorher stand dort „Nichts für diesen Filter.“, und
     * wer in dem Moment umschaltete, hielt die Karte für leer.
     */
    loading?: boolean;
}> = ({ vorgaenge, todos, scopeCtx, onChanged, loading = false }) => {
    const navigate = useNavigate();
    const { isPhone } = useResponsive();
    const [scope, setScope] = useState<ScopeKey>("persoenlich");
    // Welche Vorgänge die Karte zeigt: die am Kontakt oder die persönlichen.
    // Zwei Zustände, kein „Alle" — die volle Liste steht auf der Vorgänge-Seite.
    const [art, setArt] = useState<VorgangArtKey>(DEFAULT_ART);
    const [pill, setPill] = useState<ClusterKey>(DEFAULT_CLUSTER);
    const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
    // T-153: Suche innerhalb der gewählten Art/des Bereichs — wirkt vor den
    // Pills, damit deren Zähler die Treffer zählen.
    const [suche, setSuche] = useState("");
    const suchTokens = useMemo(() => vorgangSuchTokens(suche), [suche]);

    // P2: lange Listen client-seitig in 20er-Schritten zeigen; bei Wechsel von
    // Pill/Scope zurücksetzen.
    const [mainCount, setMainCount] = useState(PAGE_SIZE);
    useEffect(() => {
        setMainCount(PAGE_SIZE);
    }, [pill, scope, art, suche]);

    // Offene (nicht erledigte) To-dos je Vorgang gruppieren. Quelle ist die
    // bereits geladene, RBAC-gefilterte Todo-Liste — nicht das eingebettete
    // v.todos —, damit nur sichtbare To-dos erscheinen. To-dos OHNE vorgangId
    // (standalone) werden bewusst verworfen.
    const openTodosByVorgang = useMemo(() => {
        const m = new Map<number, TodoDto[]>();
        for (const t of todos) {
            const vid = t.vorgangId ?? t.vorgang?.id;
            if (vid == null) continue;
            if (t.status === "Erledigt") continue;
            const arr = m.get(vid);
            if (arr) arr.push(t);
            else m.set(vid, [t]);
        }
        for (const arr of m.values()) arr.sort((a, b) => todoSortKey(a) - todoSortKey(b));
        return m;
    }, [todos]);

    const items: VorgangItem[] = useMemo(
        () =>
            vorgaenge
                // Bei „Persönliche" gilt kein Bereich: nur die eigenen (PO 28.09.2026).
                .filter((v) => dashboardVorgangSichtbar(v, art, scope, scopeCtx))
                .map<VorgangItem>((v) => ({
                    id: v.id,
                    titel: v.titel,
                    kunde: kundeNameOf(v.kunde),
                    vorgangNbr: v.vorgangNbr ?? null,
                    status: deriveVorgangStatus(v),
                    frist: v.frist,
                    twv: v.twv,
                    erledigt: v.fortschrittErledigt,
                    gesamt: v.fortschrittGesamt,
                    openTodos: openTodosByVorgang.get(v.id) ?? [],
                }))
                .filter((it) => passtZurVorgangSuche(it, suchTokens)),
        [vorgaenge, scope, art, scopeCtx, openTodosByVorgang, suchTokens],
    );

    // Ein Vorgang matcht einen Cluster, wenn SEINE eigenen Daten passen ODER
    // eines seiner offenen To-dos passt (Vorgangs-TWV zuerst über den Sort).
    const matchVorgang = useMemo(
        () => (v: VorgangItem, key: ClusterKey): boolean =>
            matchesCluster(key, vorgangSelf(v), scopeCtx) ||
            v.openTodos.some((t) => matchesCluster(key, todoAsClusterItem(t), scopeCtx)),
        [scopeCtx],
    );

    const { counts, lists } = useMemo(() => {
        const c = {} as Record<ClusterKey, number>;
        const l = {} as Record<ClusterKey, VorgangItem[]>;
        for (const def of TODO_CLUSTERS) {
            c[def.key] = 0;
            l[def.key] = [];
        }
        for (const v of items)
            for (const def of TODO_CLUSTERS)
                if (matchVorgang(v, def.key)) {
                    c[def.key] += 1;
                    l[def.key].push(v);
                }
        for (const def of TODO_CLUSTERS) l[def.key].sort((a, b) => vorgangSortKey(a) - vorgangSortKey(b));
        return { counts: c, lists: l };
    }, [items, matchVorgang]);

    const visible = lists[pill] ?? [];

    const toggleExpand = (id: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    /** Kleine Datumszeile (Frist/TWV) — geteilt von Vorgangs- und To-do-Zeile. */
    const dueDates = (it: { frist?: string | null; twv?: string | null; status?: string }) => {
        const fristOverdue = isOverdueFrist(it);
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: isPhone ? 0 : 96, fontSize: "0.82rem" }}>
                <span
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, color: fristOverdue ? REFINED.err : REFINED.ink2, fontWeight: 600 }}
                    title={fristOverdue ? "überfällig" : undefined}
                >
                    <Flag16Regular /> {deDate(it.frist)}
                    {fristOverdue && <span style={{ fontSize: "0.7rem", fontWeight: 800, color: REFINED.err }}>überfällig</span>}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: REFINED.ink2 }}>
                    <Clock16Regular /> {deDate(it.twv)}
                </span>
            </div>
        );
    };

    // ── Ausgeklappte To-do-Unterzeile ────────────────────────────────────────
    // To-dos haben keine eigene Detailseite mehr — die Zeile öffnet den
    // Eltern-Vorgang, dessen Todos-Karte die Detailansicht enthält. `vorgangId`
    // kommt vom umgebenden VorgangItem und ist damit sicher gesetzt: `openTodos`
    // wurde über genau diese Id gruppiert (openTodosByVorgang — To-dos ohne
    // auflösbaren Vorgang sind dort bereits verworfen).
    const renderTodoSub = (t: TodoDto, idx: number, vorgangId: number) => {
        const action = <StatusChip status={t.status} />;

        const head = (
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
                <span
                    style={{
                        flexShrink: 0, width: 30, height: 30, borderRadius: 10,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        backgroundColor: "#fbf4e6", color: "#b07d12",
                    }}
                >
                    <Circle20Regular />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontFamily: REFINED.fontUI, fontWeight: 600, fontSize: "0.9rem", color: REFINED.ink,
                            ...(isPhone ? { overflowWrap: "anywhere" } : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
                        }}
                    >
                        {t.titel}
                    </div>
                    {t.art && (
                        <div style={{ fontSize: "0.8rem", color: REFINED.ink3 }}>{t.art}</div>
                    )}
                </div>
            </div>
        );

        if (isPhone) {
            return (
                <div
                    key={`todo-${t.id}`}
                    onClick={() => navigate(`/vorgaenge/${vorgangId}`)}
                    style={{
                        display: "flex", flexDirection: "column", gap: 10,
                        padding: "12px 6px", minHeight: 44,
                        borderTop: idx === 0 ? "none" : `1px solid ${REFINED.line}`, cursor: "pointer",
                    }}
                >
                    {head}
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, rowGap: 8, paddingLeft: 42 }}>
                        {dueDates(t)}
                        <div style={{ marginLeft: "auto" }}>{action}</div>
                    </div>
                </div>
            );
        }

        return (
            <div
                key={`todo-${t.id}`}
                onClick={() => navigate(`/vorgaenge/${vorgangId}`)}
                style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "10px 6px",
                    borderTop: idx === 0 ? "none" : `1px solid ${REFINED.line}`, cursor: "pointer",
                }}
            >
                {head}
                <div style={{ flexShrink: 0 }}>{dueDates(t)}</div>
                <div style={{ flexShrink: 0 }}>{action}</div>
            </div>
        );
    };

    // ── Vorgangszeile (+ Ausklapp-Chevron) ───────────────────────────────────
    const renderRow = (it: VorgangItem, idx: number) => {
        const hasTodos = it.openTodos.length > 0;
        const isOpen = expanded.has(it.id);

        const chevron = (
            <button
                type="button"
                aria-label={hasTodos ? (isOpen ? "To-dos einklappen" : "To-dos ausklappen") : undefined}
                aria-expanded={hasTodos ? isOpen : undefined}
                disabled={!hasTodos}
                title={hasTodos ? (isOpen ? "Einklappen" : `${it.openTodos.length} offene To-dos`) : "Keine offenen To-dos"}
                onClick={(e) => {
                    e.stopPropagation();
                    if (hasTodos) toggleExpand(it.id);
                }}
                style={{
                    flexShrink: 0, width: 32, height: 32, borderRadius: 999, border: "none", padding: 0,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: "transparent",
                    cursor: hasTodos ? "pointer" : "default",
                    color: hasTodos ? REFINED.ink2 : "transparent",
                }}
            >
                {isOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
            </button>
        );

        const progress = (it.gesamt ?? 0) > 0 && (
            <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 92 }}>
                <div style={{ width: "100%", height: 6, borderRadius: 999, backgroundColor: REFINED.aub50, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.round((100 * (it.erledigt ?? 0)) / (it.gesamt || 1))}%`, backgroundColor: REFINED.aub }} />
                </div>
                <span style={{ fontSize: "0.74rem", color: REFINED.ink3, fontWeight: 600 }}>
                    {it.erledigt ?? 0}/{it.gesamt}
                </span>
            </div>
        );

        const action = <StatusChip status={it.status} />;

        const head = (
            <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: 1 }}>
                <span
                    style={{
                        flexShrink: 0, width: 38, height: 38, borderRadius: 12,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        backgroundColor: REFINED.aub50, color: REFINED.aub,
                    }}
                >
                    <Layer20Regular />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontFamily: REFINED.fontUI, fontWeight: 700, fontSize: "0.98rem", color: REFINED.ink,
                            ...(isPhone ? { overflowWrap: "anywhere" } : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
                        }}
                    >
                        {it.titel}
                    </div>
                    <div
                        style={{
                            fontSize: "0.85rem", color: REFINED.ink2,
                            ...(isPhone ? { overflowWrap: "anywhere" } : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
                        }}
                    >
                        {it.kunde}
                    </div>
                </div>
            </div>
        );

        const row = isPhone ? (
            <div
                onClick={() => navigate(`/vorgaenge/${it.id}`)}
                style={{
                    display: "flex", flexDirection: "column", gap: 12,
                    padding: "14px 6px", minHeight: 44,
                    borderTop: idx === 0 ? "none" : `1px solid ${REFINED.line}`, cursor: "pointer",
                }}
            >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
                    {chevron}
                    {head}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, rowGap: 8, paddingLeft: 52 }}>
                    {dueDates(it)}
                    {progress}
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginLeft: "auto" }}>{action}</div>
                </div>
            </div>
        ) : (
            <div
                onClick={() => navigate(`/vorgaenge/${it.id}`)}
                style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "14px 6px",
                    borderTop: idx === 0 ? "none" : `1px solid ${REFINED.line}`, cursor: "pointer",
                }}
            >
                {chevron}
                {head}
                {progress}
                <div style={{ flexShrink: 0 }}>{dueDates(it)}</div>
                <div style={{ flexShrink: 0 }}>{action}</div>
            </div>
        );

        return (
            <React.Fragment key={`vorgang-${it.id}`}>
                {row}
                {isOpen && hasTodos && (
                    <div
                        style={{
                            marginLeft: isPhone ? 8 : 52,
                            marginBottom: 6,
                            paddingLeft: isPhone ? 8 : 12,
                            borderLeft: `2px solid ${REFINED.aub50}`,
                        }}
                    >
                        {it.openTodos.map((t, i) => renderTodoSub(t, i, it.id))}
                    </div>
                )}
            </React.Fragment>
        );
    };

    return (
        <DashCard
            title="Vorgänge"
            icon={<Layer20Regular />}
            // Die Art steht direkt NEBEN der Überschrift (PO 14.09.2026) — sie
            // sagt, WELCHE Vorgänge die Karte zeigt, und gehört damit zum Namen
            // der Liste. Der Bereich (WESSEN Vorgänge) bleibt als Filter rechts.
            // Beide Leisten tragen einen eigenen Namen, sonst wären es zwei
            // namenlose Knopfgruppen.
            afterTitle={<Segmented label="Art der Vorgänge" options={ART_OPTIONS} value={art} onChange={setArt} />}
            // Bei persönlichen Vorgängen gibt es nur die eigenen — der
            // Bereich-Umschalter entfällt dort (PO 28.09.2026).
            right={
                bereichWaehlbar(art) ? (
                    <Segmented label="Bereich" options={SCOPE_OPTIONS} value={scope} onChange={setScope} />
                ) : undefined
            }
        >
            {/* T-153: Suche über den Pills — Titel, Kunde, Vorgangsnummer. */}
            <div style={{ position: "relative", paddingTop: 12, width: isPhone ? "100%" : 360, maxWidth: "100%" }}>
                <span
                    aria-hidden
                    style={{
                        position: "absolute", left: 11, top: 12, bottom: 0,
                        display: "inline-flex", alignItems: "center", color: REFINED.ink3, pointerEvents: "none",
                    }}
                >
                    <Search16Regular />
                </span>
                <input
                    type="search"
                    value={suche}
                    onChange={(e) => setSuche(e.target.value)}
                    placeholder="Titel, Kunde oder Nummer suchen …"
                    aria-label="Vorgänge durchsuchen"
                    style={{
                        boxSizing: "border-box",
                        width: "100%",
                        fontFamily: REFINED.fontUI,
                        fontSize: "0.88rem",
                        padding: "8px 12px 8px 34px",
                        minHeight: isPhone ? 44 : undefined,
                        borderRadius: 9,
                        border: `1.5px solid ${REFINED.line}`,
                        backgroundColor: REFINED.white,
                        color: REFINED.ink,
                    }}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    // Phone: horizontaler Touch-Scroll-Streifen statt Umbruch.
                    flexWrap: isPhone ? "nowrap" : "wrap",
                    overflowX: isPhone ? "auto" : "visible",
                    WebkitOverflowScrolling: "touch",
                    gap: 10,
                    padding: "12px 0 6px",
                    ...(isPhone ? { marginLeft: -2, paddingLeft: 2, paddingRight: 2 } : {}),
                }}
            >
                {TODO_CLUSTERS.map((p) => (
                    <span key={p.key} style={{ flexShrink: 0, display: "inline-flex" }}>
                        <FilterPill
                            label={p.label}
                            count={counts[p.key] ?? 0}
                            active={pill === p.key}
                            onClick={() => setPill(p.key)}
                        />
                    </span>
                ))}
            </div>

            {visible.length === 0 ? (
                <EmptyHint>
                    {loading
                        ? "Lädt …"
                        : suchTokens.length > 0
                          ? "Kein Vorgang passt zur Suche."
                          : "Nichts für diesen Filter."}
                </EmptyHint>
            ) : (
                <div>
                    {visible.slice(0, mainCount).map((it, idx) => renderRow(it, idx))}
                    {visible.length > mainCount && (
                        <MehrLadenButton rest={visible.length - mainCount} onClick={() => setMainCount((c) => c + PAGE_SIZE)} />
                    )}
                </div>
            )}

        </DashCard>
    );
};

export default MeineArbeitCard;
